import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { storeTokens } from "../api/calendarApi";
import "../stylesheets/Login.css";

export default function Login() {
  const REACT_APP_GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
  const REACT_APP_GOOGLE_CLIENT_SECRET = process.env.REACT_APP_GOOGLE_CLIENT_SECRET;

  const navigate = useNavigate();

  // Scroll animation refs and state
  const titleRef = useRef(null);
  const taglineRef = useRef(null);
  const buttonRef = useRef(null);
  const logoRef = useRef(null);
  const pageRef = useRef(null);
  const aboutUsRef = useRef(null);

  const [scrolledOut, setScrolledOut] = useState(false);
  const [aboutUsVisible, setAboutUsVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const fadeOutThreshold = 400;
      setScrolledOut(scrollY > fadeOutThreshold);
      
      if (aboutUsRef.current) {
        const aboutUsPosition = aboutUsRef.current.getBoundingClientRect();
        const isVisible = aboutUsPosition.top < window.innerHeight - 150;
        setAboutUsVisible(isVisible);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const elements = [
      titleRef.current,
      taglineRef.current,
      buttonRef.current,
      logoRef.current,
    ];

    elements.forEach((el) => {
      if (el) {
        if (scrolledOut) {
          el.classList.add("scrolled-out");
        } else {
          el.classList.remove("scrolled-out");
        }
      }
    });

    if (pageRef.current) {
      if (scrolledOut) {
        pageRef.current.classList.add("fade-bg");
      } else {
        pageRef.current.classList.remove("fade-bg");
      }
    }
  }, [scrolledOut]);
  
  useEffect(() => {
    if (aboutUsRef.current) {
      if (aboutUsVisible) {
        aboutUsRef.current.classList.add("fade-in");
      } else {
        aboutUsRef.current.classList.remove("fade-in");
      }
    }
  }, [aboutUsVisible]);

  const exchangeCodeForTokens = async (code) => {
    try {
      const existingAccessToken = localStorage.getItem("google_access_token");
      const existingRefreshToken = localStorage.getItem("google_refresh_token");

      if (existingRefreshToken) {
        try {
          const response = await fetch("https://oauth2.googleapis.com/token", {
            method: "POST",
            headers: {
              "Content-Type": "application/x-www-form-urlencoded",
            },
            body: new URLSearchParams({
              client_id: REACT_APP_GOOGLE_CLIENT_ID,
              client_secret: REACT_APP_GOOGLE_CLIENT_SECRET,
              refresh_token: existingRefreshToken,
              grant_type: "refresh_token",
            }),
          });

          if (response.ok) {
            const tokens = await response.json();
            storeTokens({ ...tokens, refresh_token: existingRefreshToken });
            navigate("/home");
            return;
          }
        } catch (error) {
          console.error("Token refresh failed:", error);
        }
      }

      const response = await fetch("https://oauth2.googleapis.com/token", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams({
          code: code,
          client_id: REACT_APP_GOOGLE_CLIENT_ID,
          client_secret: REACT_APP_GOOGLE_CLIENT_SECRET,
          redirect_uri: window.location.origin,
          grant_type: "authorization_code",
        }),
      });

      const tokens = await response.json();
      storeTokens(tokens);
      navigate("/home");
    } catch (error) {
      console.error("Error exchanging code for tokens:", error);
    }
  };

  const handleGoogleLogin = () => {
    const client = window.google.accounts.oauth2.initCodeClient({
      client_id: REACT_APP_GOOGLE_CLIENT_ID,
      scope: "openid email profile https://www.googleapis.com/auth/calendar.events",
      ux_mode: "popup",
      access_type: "offline",
      prompt: "consent",
      callback: (response) => {
        if (response.code) {
          exchangeCodeForTokens(response.code);
        } else {
          console.error("Authorization failed", response);
        }
      },
    });

    client.requestCode();
  };

  return (
    <div className="login-page" ref={pageRef}>
      <div className="title-container">
        <img
          ref={logoRef}
          className="logo-img fade-on-scroll"
          src="/assets/coursepilot-logo.png"
          alt="Course Pilot"
        />
        <h1 ref={titleRef} className="main-title fade-on-scroll">
          COURSE PILOT
        </h1>
        <p ref={taglineRef} className="main-tagline fade-on-scroll">
          navigate your semester with ease
        </p>

        <div ref={buttonRef} className="google-login-container fade-on-scroll">
          <button onClick={handleGoogleLogin} className="custom-google-button">
            Sign in with Google
          </button>
        </div>
      </div>

      <div ref={aboutUsRef} className="aboutUs fade-on-scroll initially-hidden">
        <h1>ABOUT US</h1>
        <p>
        At CoursePilot, we understand the struggles students face when it comes to keeping track of assignment deadlines and exam dates. It's a small task that often slips through the cracks, leading to last-minute cramming, missed deadlines, and unnecessary stress. We've all been there, juggling multiple courses, assignments, and exams, trying to stay organized amidst the chaos.
        <br/><br/>
        That's why we decided to create a solution that automates the process and helps students stay ahead of the curve. Inspired by our own academic challenges, we built CoursePilot to eliminate the hassle of manually entering dates into calendars every semester. By harnessing the power of AI, CoursePilot intelligently schedules and organizes your coursework, allowing you to focus on what really matters, learning.
        <br/><br/>
        With CoursePilot, students no longer need to rely on memory or sticky notes. We handle the logistics, so you can stay on top of your studies without the stress.</p>
      </div>
    </div>
  );
}
